<template>
    <CollapsibleCard
        class="product-review-card mb-1"
        body-class="p-1"
        :default-collapsed="false"
    >
        <template #title>
            <div class="product-review-card__title">
                <span class="product-review-card__number">{{ number }}.</span>
                <div class="min-width-0">
                    <strong class="d-block">
                        {{ headerName }}
                    </strong>
                    <div class="product-review-card__summary mt-25">
                        <BBadge
                            v-for="badge in headerBadges"
                            :key="badge.type"
                            variant="light-secondary"
                        >
                            {{ badge.label }}
                        </BBadge>
                    </div>
                    <small v-if="summary" class="text-less-muted d-block mt-25">
                        {{ translate('Component(ResearchReviewProcess):matched-to', 'Matched to') }}:
                        {{ productSummaryLabel(summary) }}
                    </small>
                </div>
            </div>
        </template>

        <template #actions>
            <BSpinner v-if="hydrating" small variant="primary" class="mr-50" />
            <ReviewNeedsAttentionBadge :checks="evaluation.checks" />
            <BButton
                type="button"
                variant="flat-danger"
                size="sm"
                class="btn-icon mr-50"
                :class="{ 'ml-50': evaluation.status === ProductReviewCheckStatus.NEEDS_REVIEW }"
                :title="translate('Component(Button):remove', 'Remove')"
                @click.stop="$emit('remove')"
            >
                <FeatherIcon icon="Trash2Icon" size="16" />
            </BButton>
        </template>

        <BRow>
            <BCol cols="12" lg="7">
                <h6 class="mb-25">
                    {{ translate('Component(ResearchReviewProcess):extracted-product', 'Extracted from document') }}
                </h6>
                <p class="text-less-muted small mb-1">
                    {{ translate(
                        'Component(ResearchReviewProcess):extracted-product-description',
                        'Correct the product information stated in the source document.',
                    ) }}
                </p>

                <small
                    v-for="(violation, violationIndex) in generalProductViolations"
                    :key="`product-violation-${violationIndex}`"
                    class="text-danger d-block mb-50"
                >
                    {{ violation.message }}
                </small>

                <ProductAttributeRow
                    v-for="(attribute, attributeIndex) in attributes"
                    :key="`${product.extractedProductId || productIndex}-${attributeIndex}-${attribute.type}`"
                    :attribute="attribute"
                    :product-index="productIndex"
                    :attribute-index="attributeIndex"
                    :can-delete="attributes.length > 1"
                    :autofocus="focusedAttributeIndex === attributeIndex"
                    :violations="attributeViolations(attributeIndex)"
                    @input="updateAttribute(attributeIndex, $event)"
                    @remove="removeAttribute(attributeIndex)"
                />

                <div
                    v-if="allowAddAttribute && isAddingAttribute"
                    class="product-review-card__add-attribute mt-75"
                >
                    <VSelect
                        ref="attributeTypeSelect"
                        :value="null"
                        :options="translatedTypeOptions"
                        :clearable="false"
                        :placeholder="translate(
                            'Component(ResearchReviewProcess):select-attribute-type',
                            'Select attribute type',
                        )"
                        label="label"
                        @input="addAttribute"
                    />
                    <BButton
                        type="button"
                        variant="flat-secondary"
                        size="sm"
                        @click="isAddingAttribute = false"
                    >
                        {{ translate('Component(Button):cancel', 'Cancel') }}
                    </BButton>
                </div>
                <div v-else-if="allowAddAttribute" class="d-flex justify-content-end mt-75">
                    <BButton
                        type="button"
                        variant="outline-primary"
                        size="sm"
                        @click="enterAddAttributeMode"
                    >
                        <FeatherIcon icon="PlusIcon" size="13" class="mr-25" />
                        {{ translate('Component(ResearchReviewProcess):add-attribute', 'Add attribute') }}
                    </BButton>
                </div>
            </BCol>

            <BCol cols="12" lg="5" class="mt-1 mt-lg-0">
                <div class="product-review-card__match-panel">
                    <h6 class="mb-25">
                        {{ translate('Component(ResearchReviewProcess):chemity-product', 'Chemity match') }}
                    </h6>
                    <p class="text-less-muted small mb-1">
                        {{ translate(
                            'Component(ResearchReviewProcess):chemity-product-description',
                            'Confirm which Chemity product this extracted row represents.',
                        ) }}
                    </p>

                    <div v-if="recommendation" class="product-review-card__recommendation mb-1">
                        <small class="text-less-muted d-block mb-25">
                            {{ translate(
                                'Component(ResearchReviewProcess):recommended-for-review',
                                'Recommended for this review',
                            ) }}
                        </small>
                        <div class="product-review-card__recommendation-label">
                            {{ productSummaryLabel(recommendation) }}
                        </div>
                        <div class="d-flex justify-content-end mt-75">
                            <BButton
                                type="button"
                                variant="primary"
                                size="sm"
                                @click="$emit('select-link', recommendation)"
                            >
                                {{ translate('Component(ResearchReviewProcess):use-product', 'Use this product') }}
                            </BButton>
                        </div>
                    </div>

                    <template v-if="link && !isChangingLink">
                        <div class="product-review-card__selected-product">
                            <div class="font-weight-medium product-review-card__selected-product-name">
                                {{ linkedProductName }}
                            </div>
                            <small v-if="linkedProductIdentifier" class="text-less-muted d-block mt-25">
                                {{ linkedProductIdentifier }}
                            </small>
                        </div>
                        <div class="d-flex flex-wrap align-items-center justify-content-end mt-75">
                            <BButton
                                type="button"
                                variant="outline-primary"
                                size="sm"
                                class="mr-50"
                                @click="enterChangeMode"
                            >
                                {{ translate('Component(Button):change', 'Change') }}
                            </BButton>
                            <BButton
                                type="button"
                                variant="flat-danger"
                                size="sm"
                                @click="removeLink"
                            >
                                {{ translate('Component(ResearchReviewProcess):remove-product-match', 'Remove match') }}
                            </BButton>
                        </div>
                    </template>
                    <template v-else>
                        <ProductLookupSelect
                            ref="productLookupSelect"
                            :value="null"
                            :lookup="productLookup"
                            :excluded-product-ids="excludedProductIds"
                            :clearable="false"
                            :placeholder="translate(
                                'Component(ResearchReviewProcess):search-products',
                                'Search by product name or identifier',
                            )"
                            @input="selectLink"
                        />
                        <div v-if="isChangingLink" class="d-flex justify-content-end mt-50">
                            <BButton
                                type="button"
                                variant="flat-secondary"
                                size="sm"
                                @click="cancelChange"
                            >
                                {{ translate('Component(Button):cancel', 'Cancel') }}
                            </BButton>
                        </div>
                    </template>

                    <div v-if="link" class="product-review-card__link-details mt-1">
                        <div>
                            <small class="text-less-muted d-block">
                                {{ translate('Component(ResearchReviewProcess):link-type', 'Match type') }}
                            </small>
                            <span>{{ link.type || '-' }}</span>
                        </div>
                        <div>
                            <small class="text-less-muted d-block">
                                {{ translate('Component(ComplianceOverview):column-status', 'Status') }}
                            </small>
                            <span>{{ linkStatusLabel }}</span>
                        </div>
                    </div>
                    <p v-if="link && link.reason" class="text-less-muted small mt-1 mb-0">
                        {{ link.reason }}
                    </p>
                </div>
            </BCol>
        </BRow>
    </CollapsibleCard>
</template>

<script>
import {
    BBadge,
    BButton,
    BCol,
    BRow,
    BSpinner,
} from 'bootstrap-vue'
import VSelect from 'vue-select'
import CollapsibleCard from '@core/components/common/CollapsibleCard.vue'
import ReviewNeedsAttentionBadge from '../components/ReviewNeedsAttentionBadge.vue'
import ProductAttributeRow from './ProductAttributeRow.vue'
import ProductLookupSelect from './ProductLookupSelect.vue'
import {
    firstProductAttribute,
    PRODUCT_ATTRIBUTE_TYPES,
    productAttributeTypeLabel,
    productAttributes,
    productDisplayName,
} from './product-attributes'
import { ProductReviewCheckStatus } from './product-review-checker-registry'

export default {
    name: 'ProductReviewCard',
    components: {
        BBadge,
        BButton,
        BCol,
        BRow,
        BSpinner,
        CollapsibleCard,
        ProductAttributeRow,
        ProductLookupSelect,
        ReviewNeedsAttentionBadge,
        VSelect,
    },
    props: {
        number: { type: Number, required: true },
        productIndex: { type: Number, required: true },
        product: { type: Object, required: true },
        link: { type: Object, default: null },
        summary: { type: Object, default: null },
        recommendation: { type: Object, default: null },
        evaluation: { type: Object, required: true },
        productLookup: { type: Object, required: true },
        excludedProductIds: { type: Array, default: () => [] },
        hydrating: { type: Boolean, default: false },
        violations: { type: Array, default: () => [] },
        allowAddAttribute: { type: Boolean, default: true },
    },
    data() {
        return {
            ProductReviewCheckStatus,
            isChangingLink: false,
            isAddingAttribute: false,
            focusedAttributeIndex: null,
        }
    },
    computed: {
        attributes() {
            return productAttributes(this.product)
        },
        headerName() {
            return productDisplayName(this.product)
                || this.translate(
                    'Component(ResearchReviewProcess):unnamed-product',
                    'Unnamed product',
                )
        },
        headerBadges() {
            const productId = firstProductAttribute(this.product, 'product_id')
            const scipNumber = firstProductAttribute(this.product, 'scip_number')
            return [
                productId ? { type: 'product_id', label: productId.value } : null,
                scipNumber ? { type: 'scip_number', label: `SCIP: ${scipNumber.value}` } : null,
            ].filter(Boolean)
        },
        translatedTypeOptions() {
            return PRODUCT_ATTRIBUTE_TYPES.map(option => ({
                ...option,
                label: this.translate(
                    `Component(ResearchReviewProductAttributeType):${option.value}`,
                    productAttributeTypeLabel(option.value),
                ),
            }))
        },
        generalProductViolations() {
            const attributePath = '[attributes]['
            return this.violations.filter(violation => (
                violation && !String(violation.path || '').includes(attributePath)
            ))
        },
        linkedProductName() {
            if (this.summary) {
                return this.summary.name
                    || this.summary.identifier
                    || this.productSummaryLabel(this.summary)
            }
            return this.productSummaryLabel({ id: this.link && this.link.productId })
        },
        linkedProductIdentifier() {
            if (!this.summary || !this.summary.identifier || !this.summary.name) {
                return ''
            }
            return this.summary.identifier
        },
        linkStatusLabel() {
            const status = this.link && this.link.status ? this.link.status : 'uncertain'
            return this.translate(
                `Component(ResearchReviewProcessEnum):link-status-${status}`,
                status,
            )
        },
    },
    methods: {
        attributeViolations(attributeIndex) {
            const path = `[attributes][${attributeIndex}]`
            return this.violations.filter(violation => (
                violation && String(violation.path || '').includes(path)
            ))
        },
        enterAddAttributeMode() {
            if (!this.allowAddAttribute) {
                return
            }

            this.isAddingAttribute = true
            this.$nextTick(() => {
                const select = this.$refs.attributeTypeSelect
                if (!select) {
                    return
                }
                if (typeof select.focus === 'function') {
                    select.focus()
                }
                if (select.searchEl && typeof select.searchEl.focus === 'function') {
                    select.searchEl.focus()
                }
                select.open = true
            })
        },
        addAttribute(option) {
            if (!this.allowAddAttribute || !option) {
                return
            }
            const nextAttributes = this.attributes.concat({
                type: option.value,
                value: '',
                evidence: null,
            })
            this.focusedAttributeIndex = nextAttributes.length - 1
            this.isAddingAttribute = false
            this.updateAttributes(nextAttributes)
        },
        updateAttribute(index, attribute) {
            this.focusedAttributeIndex = null
            this.updateAttributes(this.attributes.map((item, itemIndex) => (
                itemIndex === index ? attribute : item
            )))
        },
        removeAttribute(index) {
            if (this.attributes.length <= 1) {
                return
            }
            this.focusedAttributeIndex = null
            this.updateAttributes(this.attributes.filter((item, itemIndex) => itemIndex !== index))
        },
        updateAttributes(attributes) {
            this.$emit('input', {
                ...this.product,
                attributes,
            })
        },
        enterChangeMode() {
            this.isChangingLink = true
            this.$nextTick(() => {
                const select = this.$refs.productLookupSelect
                if (select && typeof select.focusSearchInput === 'function') {
                    select.focusSearchInput()
                }
            })
        },
        cancelChange() {
            this.isChangingLink = false
        },
        selectLink(product) {
            if (!product) {
                return
            }
            this.isChangingLink = false
            this.$emit('select-link', product)
        },
        removeLink() {
            this.isChangingLink = false
            this.$emit('select-link', null)
        },
        translate(key, fallback) {
            const translation = this.$t(key)
            return translation && translation !== key ? translation : fallback
        },
        productSummaryLabel(summary) {
            if (!summary) {
                return ''
            }
            if (summary.name && summary.identifier) {
                return `${summary.name} · ${summary.identifier}`
            }
            return summary.name || summary.identifier || `Product #${summary.id || summary.productId}`
        },
    },
}
</script>

<style lang="scss" scoped>
.product-review-card__title {
    display: flex;
    align-items: flex-start;
    gap: .75rem;
}

.product-review-card__number {
    min-width: 1.25rem;
    font-weight: 600;
}

.product-review-card__summary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: .5rem;
}

.product-review-card__match-panel {
    height: 100%;
    padding: 1rem;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
    background: #fafafa;
}

.product-review-card__recommendation {
    padding: .75rem;
    border: 1px solid rgba(245, 128, 73, .3);
    border-radius: .357rem;
    background: rgba(245, 128, 73, .05);
}

.product-review-card__recommendation-label {
    overflow-wrap: anywhere;
}

.product-review-card__selected-product {
    padding: .75rem;
    border: 1px solid #d8d6de;
    border-radius: .357rem;
    background: #fff;
}

.product-review-card__selected-product-name {
    overflow-wrap: anywhere;
}

.product-review-card__add-attribute {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: .5rem;
}

.product-review-card__link-details {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: .75rem;
}

.min-width-0 {
    min-width: 0;
}
</style>
